import React from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { setup_product_pricing } from "services/apiSrv";
import { show_toast } from "utils/helpers";

// Validation schema
const validationSchema = Yup.object().shape({
  productPricingConfigName: Yup.string().required("Pricing Config Name is required"),
  productPricingConfigDescription: Yup.string().required("Pricing Config Description is required"),
  entityRef: Yup.string().required("Entity Reference is required"),
  periodPricing: Yup.array()
    .of(
      Yup.object().shape({
        period: Yup.string().required("Period is required"),
        periodLoadFactor: Yup.number()
          .required("Period Load Factor is required")
          .typeError("Period Load Factor must be a number"),
      })
    )
    .min(1, "At least one period is required"),
  channelPricing: Yup.array()
    .of(
      Yup.object().shape({
        channelName: Yup.string().required("Channel Name is required"),
        channelLoadFactor: Yup.number()
          .required("Channel Load Factor is required")
          .typeError("Channel Load Factor must be a number"),
      })
    )
    .min(1, "At least one channel is required"),
});

export default function PremiumPricingForm({ refetchProductDetails }) {
  const { id: productSystemRef } = useParams();

  // Initial values
  const initialValues = {
    productPricingConfigName: "",
    productPricingConfigDescription: "",
    entityRef: "",
    periodPricing: [{ period: "", periodLoadFactor: "" }],
    channelPricing: [{ channelName: "", channelLoadFactor: "" }],
  };

  // Options
  const periodOptions = [
    { value: "1", label: "1 Day" },
    { value: "30", label: "30 Days" },
     { value: "180", label: "6 Months" },
     { value: "360", label: "1 Year" },
  ];

  const channelOptions = [
    { value: "web", label: "Web" },
    { value: "mobile", label: "Mobile" },
  ];

  // Submit handler
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const periodPricingObj = values.periodPricing.reduce((acc, item) => {
      acc[item.period] = Number(item.periodLoadFactor);
      return acc;
    }, {});

    const channelPricingObj = values.channelPricing.reduce((acc, item) => {
      acc[item.channelName] = Number(item.channelLoadFactor);
      return acc;
    }, {});

    const payload = {
      productSystemRef: productSystemRef,
      productPricingConfigName: values.productPricingConfigName,
      productPricingConfigDescription: values.productPricingConfigDescription,
      entityRef: values.entityRef,
      periodPricing: periodPricingObj,
      channelPricing: channelPricingObj,
    };

    try {
      await setup_product_pricing(payload);
      show_toast("Pricing Configuration saved successfully!", "success");
      refetchProductDetails?.();
      resetForm();
    } catch (error) {
      console.error("API Error:", error);
      show_toast("Error saving pricing configuration: " + error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-sm my-4">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form>
            {/* General Pricing Config */}
            <h3 className="mb-3">Pricing Configuration</h3>
            <div className="row g-3 mb-5">
              <div className="col-md-6">
                <label htmlFor="productPricingConfigName" className="form-label fw-semibold">
                  Pricing Config Name
                </label>
                <Field name="productPricingConfigName" type="text" className="form-control" />
                <ErrorMessage
                  name="productPricingConfigName"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="productPricingConfigDescription" className="form-label fw-semibold">
                  Pricing Config Description
                </label>
                <Field
                  name="productPricingConfigDescription"
                  as="textarea"
                  className="form-control"
                />
                <ErrorMessage
                  name="productPricingConfigDescription"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="entityRef" className="form-label fw-semibold">
                  Entity Reference
                </label>
                <Field name="entityRef" type="text" className="form-control" />
                <ErrorMessage
                  name="entityRef"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </div>
            </div>

            {/* Period Pricing */}
            <h3 className="mb-3">Period Pricing</h3>
            <FieldArray name="periodPricing">
              {({ push, remove }) => (
                <>
                  {values.periodPricing.map((period, index) => (
                    <div key={index} className="row g-3 mb-3 align-items-end">
                      <div className="col-md-5">
                        <label
                          htmlFor={`periodPricing[${index}].period`}
                          className="form-label fw-semibold"
                        >
                          Period
                        </label>
                        <Field
                          as="select"
                          name={`periodPricing[${index}].period`}
                          className="form-select form-control"
                        >
                          <option value="">Select Period</option>
                          {periodOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name={`periodPricing[${index}].period`}
                          component="div"
                          className="invalid-feedback d-block"
                        />
                      </div>
                      <div className="col-md-5">
                        <label
                          htmlFor={`periodPricing[${index}].periodLoadFactor`}
                          className="form-label fw-semibold"
                        >
                          Load Factor
                        </label>
                        <Field
                          name={`periodPricing[${index}].periodLoadFactor`}
                          type="number"
                          step="0.01"
                          className="form-control"
                        />
                        <ErrorMessage
                          name={`periodPricing[${index}].periodLoadFactor`}
                          component="div"
                          className="invalid-feedback d-block"
                        />
                      </div>
                      <div className="col-md-2">
                        <button
                          type="button"
                          className="btn btn-danger w-100"
                          onClick={() => remove(index)}
                          disabled={values.periodPricing.length === 1}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary mt-3"
                    onClick={() => push({ period: "", periodLoadFactor: "" })}
                  >
                    Add Period
                  </button>
                  {/* Period Button Group */}
                  <div
                    className="day-button-group"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "16px",
                      marginTop: "24px",
                      marginBottom: "24px",
                    }}
                  >
                    {periodOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className="day-btn"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          padding: "12px 16px",
                          borderRadius: "6px",
                          border: values.periodPricing.some((p) => p.period === option.value)
                            ? "2px solid #28a745"
                            : "1px solid #ccc",
                          backgroundColor: values.periodPricing.some((p) => p.period === option.value)
                            ? "#e6f4ea"
                            : "transparent",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const index = values.periodPricing.findIndex((p) => p.period === option.value);
                          if (index === -1) {
                            push({ period: option.value, periodLoadFactor: "" });
                          } else {
                            setFieldValue(`periodPricing[${index}].period`, option.value);
                          }
                        }}
                      >
                        <span>{option.label}</span>
                        <span style={{ fontSize: "12px", color: "#555" }}>
                          {(() => {
                            const period = values.periodPricing.find((p) => p.period === option.value);
                            return period ? period.periodLoadFactor || 0 : 0;
                          })()}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </FieldArray>

            {/* Channel Pricing */}
            <h3 className="mb-3">Channel Pricing</h3>
            <FieldArray name="channelPricing">
              {({ push, remove }) => (
                <>
                  {values.channelPricing.map((channel, index) => (
                    <div key={index} className="row g-3 mb-3 align-items-end">
                      <div className="col-md-5">
                        <label
                          htmlFor={`channelPricing[${index}].channelName`}
                          className="form-label fw-semibold"
                        >
                          Channel Name
                        </label>
                        <Field
                          as="select"
                          name={`channelPricing[${index}].channelName`}
                          className="form-select form-control"
                        >
                          <option value="">Select Channel</option>
                          {channelOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name={`channelPricing[${index}].channelName`}
                          component="div"
                          className="invalid-feedback d-block"
                        />
                      </div>
                      <div className="col-md-5">
                        <label
                          htmlFor={`channelPricing[${index}].channelLoadFactor`}
                          className="form-label fw-semibold"
                        >
                          Load Factor
                        </label>
                        <Field
                          name={`channelPricing[${index}].channelLoadFactor`}
                          type="number"
                          step="0.01"
                          className="form-control"
                        />
                        <ErrorMessage
                          name={`channelPricing[${index}].channelLoadFactor`}
                          component="div"
                          className="invalid-feedback d-block"
                        />
                      </div>
                      <div className="col-md-2">
                        <button
                          type="button"
                          className="btn btn-danger w-100"
                          onClick={() => remove(index)}
                          disabled={values.channelPricing.length === 1}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary mt-3"
                    onClick={() => push({ channelName: "", channelLoadFactor: "" })}
                  >
                    Add Channel
                  </button>
                  {/* Channel Button Group */}
                  <div
                    className="button-group"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "16px",
                      marginTop: "24px",
                      marginBottom: "24px",
                    }}
                  >
                    {channelOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className="custom-btn"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          padding: "12px 16px",
                          borderRadius: "6px",
                          border: values.channelPricing.some((c) => c.channelName === option.value)
                            ? "2px solid #28a745"
                            : "1px solid #ccc",
                          backgroundColor: values.channelPricing.some((c) => c.channelName === option.value)
                            ? "#e6f4ea"
                            : "transparent",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const index = values.channelPricing.findIndex((c) => c.channelName === option.value);
                          if (index === -1) {
                            push({ channelName: option.value, channelLoadFactor: "" });
                          } else {
                            setFieldValue(`channelPricing[${index}].channelName`, option.value);
                          }
                        }}
                      >
                        <span>{option.label}</span>
                        <span style={{ fontSize: "12px", color: "#555" }}>
                          {(() => {
                            const channel = values.channelPricing.find((c) => c.channelName === option.value);
                            return channel ? channel.channelLoadFactor || 0 : 0;
                          })()}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </FieldArray>

            {/* Save Button */}
            <div className="d-flex justify-content-end mt-4" style={{ gap: "24px" }}>
              <button
                type="submit"
                className="btn btn-success px-4 py-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save All Pricing"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}